import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Grid, Typography } from "@material-ui/core";

import InfoWidget from "./InfoWidget";
import WalletWidget from "./WalletWidget";
import DevicesWidget from "./DevicesWidget";
import HistoryWidget from "./HistoryWidget";

import { SectionGridContainer, SectionGridLayout } from "../../components/Layout";
import { LoadingOverlay } from "../../components/Navigation/LoadingOverlay";
import { RiderContext } from "../../context";
import { useTestIds } from "../../hooks/useTestIds";

const RiderProfile = () => {
  const { t } = useTranslation();
  const { riderId } = useParams();

  const [loading, setLoading] = useState(false);

  const {
    setActiveRider,
    activeRider,
    fetchWallet,
    fetchCustomerCoupons,
    fetchActivePass,
    fetchActiveUserDevices,
  } = useContext(RiderContext);

  const testIds = useTestIds("rider-profile");

  // Watches URL for a change in riderId and updates context for WalletWidget & DevicesWidget
  useEffect(() => {
    const fetchesForCurrentRider = async () => {
      const promises = [
        fetchWallet(riderId),
        fetchActivePass(riderId),
        fetchCustomerCoupons(riderId),
        fetchActiveUserDevices(riderId),
      ];
      await Promise.all(promises);
    };
    fetchesForCurrentRider().catch((error) =>
      console.error("Error fetching data for current rider: ", error),
    );
  }, [riderId]);

  useEffect(() => {
    const waitForRider = async () => {
      setLoading(true);
      try {
        await setActiveRider(riderId);
      } catch (error) {
        setLoading(false);
      }
      setLoading(false);
    };
    if (!riderId || riderId !== activeRider?.id) {
      waitForRider();
    }

    // on unmount, reset all fetched data
    return () => {
      setActiveRider(null);
      fetchWallet(null);
      fetchCustomerCoupons(null);
      fetchActivePass(null);
      fetchActiveUserDevices(null);
    };
  }, [riderId]);

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!activeRider) {
    return (
      <SectionGridLayout>
        <SectionGridContainer spaceContents>
          <Typography data-testid={testIds.requestError} variant="body1">
            {t("Riders.RiderProfile.RequestError")}
          </Typography>
        </SectionGridContainer>
      </SectionGridLayout>
    );
  }

  return (
    <SectionGridLayout direction="row" gridProps={{ alignItems: "flex-start", spacing: 4 }}>
      <Grid item xs={12} sm={5} md={4} lg={3}>
        <InfoWidget />
        <WalletWidget />
        <DevicesWidget />
      </Grid>
      <HistoryWidget />
    </SectionGridLayout>
  );
};

export default RiderProfile;
